// src/lib/google-analytics.ts
// Google Analytics side of the analytics consent gate. gtag.js cannot be
// unloaded once injected, so withdrawal flips the documented `ga-disable-<id>`
// kill switch and drops the cookies it already set.
//
// Also the single place the additive SEO measurement events are sent from, so
// the consent gate and those events can never disagree about whether GA is
// allowed to receive anything.

import { siteDetails } from "@/data/siteDetails";
import {
  formatStamped,
  isFreshSignupConfirmation,
  isLiveGaHost,
  parseReferringCta,
  REFERRING_CTA_KEY,
  SIGNUP_INTENT_KEY,
  SIGNUP_OK_KEY,
} from "@/lib/analytics/commercial-events";

const GA_ID = siteDetails.googleAnalyticsId;
const DISABLE_KEY = `ga-disable-${GA_ID}`;

function setDisabled(disabled: boolean): void {
  (window as unknown as Record<string, boolean>)[DISABLE_KEY] = disabled;
}

// _ga, _ga_<container>, _gid, _gat*. Cleared on the exact host and on the
// registrable domain, since gtag writes to the latter (".niftipay.com").
function clearGaCookies(): void {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [host, parts.length > 2 ? `.${parts.slice(-2).join(".")}` : `.${host}`];
  for (const entry of document.cookie.split("; ")) {
    const name = entry.split("=")[0];
    if (!/^_ga|^_gid$|^_gat/.test(name)) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}`;
    }
    document.cookie = `${name}=; Max-Age=0; Path=/`;
  }
}

export function stopGoogleAnalytics(): void {
  if (typeof window === "undefined") return;
  setDisabled(true);
  clearGaCookies();
}

export function resumeGoogleAnalytics(): void {
  if (typeof window === "undefined") return;
  setDisabled(false);
}

/* ───────── additive SEO measurement events (see lib/analytics/commercial-events.ts) ───────── */

// The only shape these events send: flat string parameters. No ids, no query
// strings, no form values — see `ctaDestinationType`, which returns a type and
// never an href.
export type GaEventParams = Record<string, string>;

interface GaWindow {
  gtag?: (command: "event", name: string, params: GaEventParams) => void;
  /** Off the live host, the events land here instead of being sent. */
  __npGaDryRun?: { name: string; params: GaEventParams }[];
}

function gaWindow(): GaWindow {
  return window as unknown as GaWindow;
}

/**
 * Sends one additive GA4 event, or records it in `window.__npGaDryRun` when
 * the page is not on a live marketing host (staging, Vercel previews,
 * localhost).
 *
 * Returns false when there is no `gtag` to send through, which is the one case
 * a caller may want to retry: `<GoogleAnalyticsConsent>` injects gtag.js from
 * its own Effect, so a component mounting on the same commit can run first.
 *
 * No consent check of its own: a visitor who rejected the analytics category
 * either never had gtag.js injected (`gtag` is undefined here) or had
 * `ga-disable-<id>` flipped by `stopGoogleAnalytics()` above, so nothing
 * leaves the page either way. Adding a second gate would be a second source
 * of truth for the same decision.
 */
export function sendGaEvent(name: string, params: GaEventParams): boolean {
  if (typeof window === "undefined") return false;

  if (!isLiveGaHost(window.location.hostname)) {
    const win = gaWindow();
    win.__npGaDryRun ??= [];
    win.__npGaDryRun.push({ name, params });
    return true;
  }

  const gtag = gaWindow().gtag;
  if (typeof gtag !== "function") return false;
  gtag("event", name, params);
  return true;
}

// `sessionStorage` throws outright in some privacy modes, and these two reads
// only decide whether an additive event fires. A failure is never allowed to
// break a click handler or the sign-up submit.
function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the event still fires, just without attribution.
  }
}

function clearSession(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing was stored to clear.
  }
}

/** Remembers which CTA sent this visitor to `/sign-up`, for `sign_up`. */
export function rememberReferringCta(placement: string): void {
  if (typeof window === "undefined") return;
  writeSession(REFERRING_CTA_KEY, formatStamped(placement, Date.now()));
}

/**
 * The remembered CTA placement, or `"unknown"` for a direct arrival or a click
 * older than the 30 minutes the registered dimension is defined over.
 */
export function readReferringCta(): string {
  if (typeof window === "undefined") return "unknown";
  return parseReferringCta(readSession(REFERRING_CTA_KEY), Date.now());
}

/**
 * Records that the sign-up API accepted a registration, for the `/verify-email`
 * mount to turn into one `sign_up`. Written instead of sending the event from
 * the success branch, where the hard navigation that follows can drop the hit.
 */
export function markSignupConfirmed(): void {
  if (typeof window === "undefined") return;
  writeSession(SIGNUP_OK_KEY, formatStamped("1", Date.now()));
}

/** True while an unconsumed, under-30-minute sign-up confirmation is stored. */
export function hasSignupConfirmation(): boolean {
  if (typeof window === "undefined") return false;
  return isFreshSignupConfirmation(readSession(SIGNUP_OK_KEY), Date.now());
}

/**
 * Consumes the confirmation, so `sign_up` can only ever be sent once per
 * registration however many times `/verify-email` is opened.
 */
export function consumeSignupConfirmation(): void {
  if (typeof window === "undefined") return;
  clearSession(SIGNUP_OK_KEY);
}

/**
 * True the first time it is called in a browsing session, false afterwards —
 * so `signup_intent` counts one interaction per session, not one per page
 * load or per focused field.
 */
export function claimSignupIntent(): boolean {
  if (typeof window === "undefined") return false;
  if (readSession(SIGNUP_INTENT_KEY) !== null) return false;
  writeSession(SIGNUP_INTENT_KEY, "1");
  return true;
}
